import React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {emotionStyled, keyframes} from '@packages/utilities/styled';

import {tokens} from '../../themes';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

export type SpinnerProps = {
  /**
   * The color of the `Spinner`
   */
  colorVariant?: 'white' | 'default';
  /**
   * The width and height dimensions in px of the `Spinner`
   */
  size?: number;
} & HTMLAttributes &
  SpringTooling;

const spin = keyframes({
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
});

const SpinnerView = emotionStyled('div')(
  {
    animation: `${spin} 0.6s linear infinite`,
    svg: {
      display: 'block',
      width: '100%',
      height: '100%',
    },
  },
  {
    componentName: 'Spinner',
    variants: {
      colorVariant: {
        default: {
          color: tokens.colors.textPrimary,
        },
        white: {
          color: tokens.colors.white,
        },
      },
    },
    defaultVariants: {
      colorVariant: 'default',
    },
  }
);

/**
 * # Spinner
 *
 * An animated icon used to indicate an active process, e.g. content loading
 *
 * ## Usage
 *
 * ```jsx
 * import {Spinner} from '@packages/systems/spring';
 *
 * const Example = () => <Spinner />
 * ```
 *
 * @component
 */
export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({size = 30, style = emptyObject, ...rest}: SpinnerProps, ref) => {
    return (
      <SpinnerView
        {...rest}
        ref={ref}
        style={{width: size, height: size, ...style}}
      >
        {/* @ts-expect-error - TS2322 - Type '{ children: Element[]; "aria-label": string; "aria-valuemax": string; "aria-valuemin": string; role: "progressbar"; viewBox: string; xmlns: string; }' is not assignable to type 'SVGProps<SVGSVGElement>'. */}
        <svg
          aria-label="Loading..."
          aria-valuemax="100"
          aria-valuemin="0"
          role="progressbar"
          viewBox="0 0 30 30"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 30a15 15 0 110-30 15 15 0 010 30zm0-3a12 12 0 100-24 12 12 0 000 24z"
            fill="currentColor"
            opacity=".2"
          />
          <path
            d="M0 15A15 15 0 0115 0v3A12 12 0 003 15H0z"
            fill="currentColor"
            opacity=".75"
          />
        </svg>
      </SpinnerView>
    );
  }
);

Spinner.displayName = 'Spinner';
Spinner.toString = SpinnerView.toString;

export default Spinner;
